import moment from 'moment'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getPromotions } from '../modules/Promotions/selectors.js'

const filterPromotion = ({
  includeCreditPromotions,
  promotion: {
    promotion: { discount, credit },
    createdAt
  },
  retrieve
}: {
  promotion: any
  includeCreditPromotions: boolean
  retrieve: 'expired' | 'available'
}) => {
  if (includeCreditPromotions && credit !== null) {
    return true
  }

  if (credit !== null && discount === null) {
    return true
  }

  const start = moment(createdAt)
  const end = moment(start).add(discount.duration, 'days')
  const isExpired = moment().isAfter(end)

  if (retrieve === 'expired') {
    return isExpired
  }

  return !isExpired
}

export const getExpirationDate = ({
  promotion: { discount, credit },
  createdAt
}) => {
  const promo = discount ?? credit

  if (!promo) {
    return null
  }

  const start = moment(createdAt)
  return moment(start)
    .add(promo.duration, 'days')
    .format('yyyy/MM/DD')
}

export const usePromotions = (
  { includeCreditPromotions = false }: { includeCreditPromotions: boolean } = {
    includeCreditPromotions: false
  }
) => {
  const storePromotions = useSelector(state => getPromotions(state))
  // TODO: instead of use redux for this, we should have a query to get the promotions and just use `useQuery`
  const isLoading = storePromotions === null

  const availablePromotions = useMemo(() => {
    const getAvailablePromos = () => {
      if (!storePromotions || storePromotions.length === 0) {
        return []
      }

      return storePromotions
        .filter(promotion =>
          filterPromotion({
            promotion,
            includeCreditPromotions,
            retrieve: 'available'
          })
        )
        .sort((p1, p2) =>
          moment(getExpirationDate(p1)).diff(moment(getExpirationDate(p2)))
        )
    }

    return getAvailablePromos()
  }, [storePromotions, includeCreditPromotions])

  const expiredPromotions = useMemo(() => {
    const getExpiredPromos = () => {
      if (!storePromotions) {
        return []
      }

      return storePromotions
        .filter(promotion =>
          filterPromotion({
            promotion,
            includeCreditPromotions,
            retrieve: 'expired'
          })
        )
        .sort((p1, p2) =>
          moment(getExpirationDate(p2)).diff(moment(getExpirationDate(p1)))
        )
    }

    return getExpiredPromos()
  }, [storePromotions, includeCreditPromotions])

  const promotions = useMemo(() => {
    return [...availablePromotions, ...expiredPromotions]
  }, [availablePromotions, expiredPromotions])

  return {
    storePromotions,
    promotions,
    availablePromotions,
    isLoading
  }
}
